import { useMemo, useState } from 'react';

import type { Arc } from './arcTypes';
import { differs, type WorkingDraft } from './draftEdits';

/**
 * The working copy of the ARC: what the page shows and edits until Save
 * Draft. Before the first edit it is the saved draft itself; `dirty` says
 * whether it now differs from it; `discard` goes back to the saved draft,
 * which is also what follows a successful save.
 *
 * A working copy of another ARC is never shown: switching organization
 * changes the saved draft without leaving the page, and the old copy's
 * sections would be sent to the new organization's ARC.
 */
export function useDraft(saved: Arc | undefined) {
  const [held, setHeld] = useState<WorkingDraft | null>(null);
  const working = held && saved && held.arc.id === saved.id ? held : null;
  const draft: WorkingDraft | null = working ?? (saved ? { arc: saved } : null);
  // Two serialisations of the whole ARC: done when either side changes, not
  // on every render of a page that re-renders for every menu and toggle.
  const dirty = useMemo(
    () => Boolean(working && saved && differs(working, saved)),
    [working, saved],
  );

  /** Applies one edit from `draftEdits.ts` to the working copy. */
  const edit = (change: (current: WorkingDraft) => WorkingDraft) => {
    if (!saved) return;
    setHeld((current) =>
      change(current && current.arc.id === saved.id ? current : { arc: saved }),
    );
  };

  return { draft, dirty, edit, discard: () => setHeld(null) };
}
